import { buildLocalePath, type MultilingualLocale } from "../localePath"
import { ORGANIZATION_ID, WEBSITE_ID, WWW_ORIGIN } from "./constants"
import { getJsonLdLanguage, type JsonLdNode } from "./core"

export interface WebPageOptions {
  /** 页面完整 URL（canonical） */
  url: string
  /** 页面标题，建议与 <title> 一致 */
  name: string
  description?: string
  /** BCP-47 语言码，如 "en"、"zh-CN" */
  inLanguage?: string
  /** 页面类型，默认 WebPage；首页可用 WebPage，品目/列表页用 CollectionPage */
  type?: "WebPage" | "CollectionPage" | "AboutPage" | "ContactPage" | "FAQPage"
  /** 页面主体实体 @id（如 Dataset、Article），存在时输出 mainEntity */
  mainEntityId?: string
  /** 页面实际受限区域的 CSS 类选择器；传入后输出 isAccessibleForFree:false 和 hasPart。 */
  restrictedCssSelector?: string
  /** 主图 URL */
  primaryImageUrl?: string
  /** 主图节点 @id（引用图中已输出的 ImageObject），与 primaryImageUrl 二选一且优先 */
  primaryImageId?: string
  primaryImageWidth?: number
  primaryImageHeight?: number
  /** 面包屑节点 @id，存在时自动关联 */
  breadcrumbId?: string
  /** WebSite @id，默认主站 */
  websiteId?: string
  /** Organization @id，默认主站 */
  organizationId?: string
}

/** 页面级 WebPage 节点（每个页面输出，@id = canonical + #{type 小写}）。 */
export function buildWebPageJsonLd(options: WebPageOptions): JsonLdNode {
  const {
    url,
    name,
    description,
    inLanguage = "en",
    type = "WebPage",
    mainEntityId,
    restrictedCssSelector,
    primaryImageUrl,
    primaryImageId,
    primaryImageWidth,
    primaryImageHeight,
    breadcrumbId,
    websiteId = WEBSITE_ID,
    organizationId = ORGANIZATION_ID,
  } = options

  const node: JsonLdNode = {
    "@type": type,
    "@id": `${url}#${type.toLowerCase()}`,
    url,
    name,
    inLanguage: getJsonLdLanguage(inLanguage),
    isPartOf: { "@id": websiteId },
    publisher: { "@id": organizationId },
  }
  if (description) node.description = description
  if (mainEntityId) node.mainEntity = { "@id": mainEntityId }
  if (restrictedCssSelector) {
    node.isAccessibleForFree = false
    node.hasPart = {
      "@type": "WebPageElement",
      isAccessibleForFree: false,
      cssSelector: restrictedCssSelector,
    }
  }
  if (breadcrumbId) node.breadcrumb = { "@id": breadcrumbId }
  if (primaryImageId) {
    node.primaryImageOfPage = { "@id": primaryImageId }
  } else if (primaryImageUrl) {
    node.primaryImageOfPage = {
      "@type": "ImageObject",
      url: primaryImageUrl,
      ...(primaryImageWidth ? { width: primaryImageWidth } : {}),
      ...(primaryImageHeight ? { height: primaryImageHeight } : {}),
    }
  }
  return node
}

export interface BreadcrumbItem {
  name: string
  /** 末级可省略 URL（Google 允许末级无 item） */
  url?: string
}

/** 面包屑节点。@id = 当前页 canonical + #breadcrumb。 */
export function buildBreadcrumbJsonLd(items: BreadcrumbItem[], pageUrl: string): JsonLdNode {
  return {
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: item.url } : {}),
    })),
  }
}

export interface ItemListEntry {
  name: string
  url?: string
}

/** ItemList 节点（新闻列表等）。@id = `${pageUrl}#latest-news-list`。 */
export function buildItemListJsonLd(
  entries: ItemListEntry[],
  pageUrl: string,
  options?: { idSuffix?: string; name?: string }
): JsonLdNode {
  return {
    "@type": "ItemList",
    "@id": `${pageUrl}${options?.idSuffix ?? "#latest-news-list"}`,
    ...(options?.name ? { name: options.name } : {}),
    itemListElement: entries.map((entry, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: entry.name,
      ...(entry.url ? { url: entry.url } : {}),
    })),
  }
}

/** 根据 locale 生成页面 canonical URL（与 hreflang 规则一致：英文无前缀）。 */
export function buildJsonLdPageUrl(
  locale: string,
  pathnameSuffix: string,
  options?: { origin?: string; omitDefaultLocalePrefix?: boolean }
): string {
  const origin = options?.origin ?? WWW_ORIGIN
  const omitDefaultLocalePrefix = options?.omitDefaultLocalePrefix ?? true
  const path = buildLocalePath(locale as MultilingualLocale, pathnameSuffix, { omitDefaultLocalePrefix })
  return `${origin}${path}`
}
